import { makeAssignment } from './lobbyScheduleEditorOps'
import { timeToMinutes } from './lobbyScheduleUtils'
import type {
  ScheduleAssignmentDto, ScheduleAttendeeDto, ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'

export interface SuggestResult {
  meetings: ScheduleMeetingDto[]
  stagedCount: number
  meetingsAffected: number
}

interface Span { date: string; start: number; end: number }

/** Fill EXISTING meetings with each legislator's conflict-free constituents from the pool.
 *  Never invents times, never creates meetings. Returns new local meetings (staged, unsaved
 *  assignments carry `new:` ids via makeAssignment) plus counts for a toast. Meetings are
 *  processed in chronological order so overlapping-double-booking is resolved earliest-first. */
export function suggestConstituentAssignments(
  meetings: ScheduleMeetingDto[],
  attendees: ScheduleAttendeeDto[],
  legislators: ScheduleLegislatorRowDto[],
): SuggestResult {
  const legislatorById = new Map(legislators.map((l) => [l.legislatorId, l] as const))

  // Each attendee's occupied [start,end) spans per date (existing + newly staged).
  const busy = new Map<string, Span[]>()
  for (const m of meetings)
    for (const a of m.assignments) {
      const arr = busy.get(a.attendeeId) ?? []
      arr.push({ date: m.meetingDate, start: timeToMinutes(m.startTime), end: timeToMinutes(m.endTime) })
      busy.set(a.attendeeId, arr)
    }

  const overlaps = (spans: Span[] | undefined, date: string, start: number, end: number) =>
    !!spans?.some((s) => s.date === date && start < s.end && s.start < end)

  const order = [...meetings].sort((a, b) =>
    a.meetingDate === b.meetingDate
      ? timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
      : a.meetingDate.localeCompare(b.meetingDate))

  const additions = new Map<string, ScheduleAssignmentDto[]>()
  let stagedCount = 0

  for (const m of order) {
    const legislator = legislatorById.get(m.legislatorId)
    if (!legislator) continue
    const start = timeToMinutes(m.startTime)
    const end = timeToMinutes(m.endTime)
    const already = new Set(m.assignments.map((a) => a.attendeeId))
    const candidates = attendees.filter((a) =>
      a.constituentOfLegislatorIds.includes(m.legislatorId) &&
      !already.has(a.attendeeId) &&
      !overlaps(busy.get(a.attendeeId), m.meetingDate, start, end))
    if (candidates.length === 0) continue
    additions.set(m.id, candidates.map((a) => makeAssignment(a, legislator)))
    stagedCount += candidates.length
    for (const a of candidates) {
      const arr = busy.get(a.attendeeId) ?? []
      arr.push({ date: m.meetingDate, start, end })
      busy.set(a.attendeeId, arr)
    }
  }

  const updated = meetings.map((m) =>
    additions.has(m.id) ? { ...m, assignments: [...m.assignments, ...additions.get(m.id)!] } : m)

  return { meetings: updated, stagedCount, meetingsAffected: additions.size }
}
